/**
 * @description Composable for handling report export functionality
 */

import { reactive, toRefs } from 'vue'
import { useStore } from '@state/base'
import { useRoute } from 'vue-router'
import proxy from '@common/lib/http-common'

// Import API URL from config
import { saveReportsURL } from '@src/modules/analytics/config/api-utils'

export default function useReportExport() {
  const route = useRoute()
  const { getters } = useStore()

  const state = reactive({
    loading: false,
    error: null,
  })

  /**
   * Exports a report by making API call to generate the report
   * @param {Object} params - Parameters for the report export
   * @param {string} params.competitorReportId - ID of the competitor report
   * @param {string} params.dateRange - Date range for the report
   * @param {string} params.action - Action to perform (render, email, etc.)
   * @returns {Promise<Object>} - Response from the API
   */
  const exportReport = async (params) => {
    state.loading = true
    state.error = null

    try {
      // Prepare payload based on curl example and route information
      const payload = {
        type: 'competitor',
        competitor_report_id: params.competitorReportId,
        date: params.dateRange,
        action: params.action || 'render',
        platform_type: route.name.includes('facebook') ? 'facebook' :
                       route.name.includes('instagram') ? 'instagram' : 'facebook',
        workspace_id: getters.getActiveWorkspace._id,
        user_id: getters.getProfile._id
      }

      // Using proxy which automatically handles token authorization
      const { data } = await proxy.post(saveReportsURL, payload)
      return data
    } catch (error) {
      console.error('Export Report Error:', error)
      state.error = error.response?.data?.message || 'Failed to export report'
      throw error
    } finally {
      state.loading = false
    }
  }

  return {
    ...toRefs(state),
    exportReport
  }
}
